/**
 * The per-tab sequence ledger shared by the telemetry rings (lib/logRingBuffer.js):
 * the domain ring and the trace ring draw seqs from one counter and share one
 * acknowledged watermark, dropped count and discarded-through floor.
 *
 * - Every seq allocation and every ack writes `SEQ_KEY` synchronously: a reload must
 *   never reuse a seq the server may have accepted (its event ids derive from it).
 * - Evicting an unsent entry only counts it as dropped. The watermark does not jump
 *   over it, because the other ring may still hold unsent entries below it; a batch
 *   already spans seq gaps (`fromSeq = lastAckedSeq + 1`), so the gap is acknowledged
 *   with the next batch.
 * - A failed ring write is counted per ring (`telemetry.persist_failed`); the entries
 *   are still in memory, so they are not dropped.
 * - Entries acknowledged unsent because the stream was off (auto-acknowledged at
 *   allocation, or the backlog acknowledged when it turned off) are counted as
 *   skipped, so the next enable can report the seq gap they leave
 *   (`telemetry.dropped {reason: "pre_enable"}`).
 */

export const SEQ_KEY = "sooth.chatboard.log-ring.seq";
/** sessionStorage key of the telemetry tab id (services/telemetry.js). */
export const TELEMETRY_TAB_KEY = "sooth.chatboard.telemetry-tab";

function defaultStorage() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** `ranges` (sorted `[from, to]`) with `seqs` added, adjacent and overlapping merged. */
function mergeRanges(ranges, seqs) {
  const all = [...ranges, ...seqs.map((s) => [s, s])].sort((x, y) => x[0] - y[0]);
  const merged = [];
  for (const [from, to] of all) {
    const last = merged[merged.length - 1];
    if (last && from <= last[1] + 1) last[1] = Math.max(last[1], to);
    else merged.push([from, to]);
  }
  return merged;
}

const validRanges = (list) =>
  list.filter((r) => Array.isArray(r) && Number.isInteger(r[0]) && Number.isInteger(r[1]));

export class SeqLedger {
  /** @param {{storage?: Storage | null}} [opts] omitted: sessionStorage, read at each use */
  constructor({ storage } = {}) {
    this._storage = storage;
    this.nextSeq = 1;
    this.lastAckedSeq = 0;
    this.discardedThrough = 0;
    this.droppedCount = 0;
    /**
     * Unacknowledged seqs already counted as dropped (evicted unsent), as sorted
     * `[from, to]` ranges: a reload must not count them again. Pruned as the
     * watermark passes them.
     */
    this.counted = [];
    /** Unacknowledged seqs a ring's `clear()` removed, as sorted ranges. */
    this.cleared = [];
    /** Last seq restored from storage, before this page pushed anything. */
    this.hydratedThroughSeq = 0;
    /** While true, every allocated seq is acknowledged at once (telemetry disabled). */
    this.autoAck = false;
    /** Seqs acknowledged unsent while the stream was off, not yet reported. */
    this.skippedCount = 0;
    /** Highest of those seqs. */
    this.skippedThrough = 0;
    /** Failed ring writes since the last report, by ring name. */
    this.persistFailed = {};
    /** The rings drawing from this ledger (registered by LogRingBuffer). */
    this.rings = [];
  }

  get storage() {
    return this._storage === undefined ? defaultStorage() : this._storage;
  }

  /** Raise the counters to the persisted ones; `base` carries a legacy ring payload's. */
  hydrate(base = null) {
    if (base && Number.isInteger(base.next_seq) && base.next_seq > 0) {
      this.nextSeq = Math.max(this.nextSeq, base.next_seq);
      if (Number.isInteger(base.last_acked_seq)) this.lastAckedSeq = base.last_acked_seq;
      if (Number.isInteger(base.discarded_through)) this.discardedThrough = base.discarded_through;
      if (Number.isInteger(base.dropped)) this.droppedCount = base.dropped;
    }
    let seq;
    try {
      seq = JSON.parse(this.storage?.getItem(SEQ_KEY) || "null");
    } catch {
      return;
    }
    // Equal counters still carry a newer watermark when only a ring write failed.
    if (!seq || !Number.isInteger(seq.next_seq) || seq.next_seq < this.nextSeq) return;
    this.nextSeq = seq.next_seq;
    if (Number.isInteger(seq.last_acked_seq)) {
      this.lastAckedSeq = Math.min(
        Math.max(this.lastAckedSeq, seq.last_acked_seq),
        this.nextSeq - 1,
      );
    }
    if (Number.isInteger(seq.discarded_through)) {
      this.discardedThrough = Math.max(this.discardedThrough, seq.discarded_through);
    }
    if (Number.isInteger(seq.dropped)) this.droppedCount = seq.dropped;
    if (Number.isInteger(seq.skipped)) this.skippedCount = seq.skipped;
    if (Number.isInteger(seq.skipped_through)) this.skippedThrough = seq.skipped_through;
    if (Array.isArray(seq.counted)) this.counted = validRanges(seq.counted);
    if (Array.isArray(seq.cleared)) this.cleared = validRanges(seq.cleared);
  }

  persist() {
    try {
      this.storage?.setItem(
        SEQ_KEY,
        JSON.stringify({
          next_seq: this.nextSeq,
          last_acked_seq: this.lastAckedSeq,
          discarded_through: this.discardedThrough,
          dropped: this.droppedCount,
          skipped: this.skippedCount,
          skipped_through: this.skippedThrough,
          counted: this.counted,
          cleared: this.cleared,
        }),
      );
    } catch {
      // The counter could not be saved, so a reload could reuse a seq the server
      // has accepted. Drop the tab id instead: the reloaded page takes a new one,
      // and its seqs no longer collide with this tab's event ids.
      try {
        this.storage?.removeItem(TELEMETRY_TAB_KEY);
      } catch {
        /* best effort */
      }
    }
  }

  /** The next seq; persisted before it is handed out. */
  allocate() {
    const seq = this.nextSeq++;
    if (this.autoAck) {
      this.lastAckedSeq = seq;
      this.skippedCount += 1;
      this.skippedThrough = seq;
    }
    this.persist();
    return seq;
  }

  /** Count `n` unsent entries (highest seq `through`) acknowledged because the stream turned off. */
  noteSkipped(n, through) {
    if (!(n > 0)) return;
    this.skippedCount += n;
    this.skippedThrough = Math.max(this.skippedThrough, through);
    this.persist();
  }

  /** Return and reset the skipped-entry count, or null when none was skipped. */
  takeSkipped() {
    if (this.skippedCount === 0) return null;
    const report = { count: this.skippedCount, through: this.skippedThrough };
    this.skippedCount = 0;
    this.skippedThrough = 0;
    this.persist();
    return report;
  }

  /** Count the evicted, unsent entries `seqs` (ascending) as dropped. */
  noteEvictedSeqs(seqs) {
    if (seqs.length === 0) return;
    this.counted = mergeRanges(this.counted, seqs);
    this.noteEvicted(seqs.length, seqs[seqs.length - 1]);
  }

  /**
   * Unsent seqs a ring's `clear()` removed above the watermark (it cannot pass the
   * other ring's unsent entries): neither sent nor lost.
   */
  noteCleared(seqs) {
    if (seqs.length === 0) return;
    this.cleared = mergeRanges(this.cleared, seqs);
    this.persist();
  }

  /** Whether `seq` was removed by a ring's `clear()`. */
  isCleared(seq) {
    return this.cleared.some(([f, t]) => seq >= f && seq <= t);
  }

  /**
   * Forget counted seqs the watermark has passed. Called by the stream once no request
   * is in flight: until then a rejection of an in-flight batch must still see them.
   */
  pruneCounted() {
    const before = this.counted.length + this.cleared.length;
    this.counted = this.counted.filter(([, to]) => to > this.lastAckedSeq);
    this.cleared = this.cleared.filter(([, to]) => to > this.lastAckedSeq);
    if (this.counted.length + this.cleared.length !== before) this.persist();
  }

  /** Whether `seq` was already counted as dropped. */
  isCounted(seq) {
    return this.counted.some(([f, t]) => seq >= f && seq <= t);
  }

  /** Count `n` lost, unsent entries (highest seq `through`) as dropped. */
  noteEvicted(n, through) {
    if (!(n > 0)) return;
    this.droppedCount += n;
    this.discardedThrough = Math.max(this.discardedThrough, through);
    this.persist();
  }

  /** Advance the acknowledged watermark to `seq` (never backwards). */
  ack(seq) {
    if (Number.isInteger(seq) && seq > this.lastAckedSeq) {
      this.lastAckedSeq = Math.min(seq, this.nextSeq - 1);
      this.persist();
    }
  }

  /** Move the watermark back to `seq` (a tab-identity change resends past it). */
  rewindAck(seq) {
    if (Number.isInteger(seq) && seq >= 0 && seq < this.lastAckedSeq) {
      this.lastAckedSeq = seq;
      this.persist();
    }
  }

  ackAll() {
    this.ack(this.nextSeq - 1);
  }

  /** Lowest unacknowledged seq held by any ring other than `except`, or Infinity. */
  lowestUnackedExcept(except) {
    let low = Infinity;
    for (const r of this.rings) {
      if (r === except) continue;
      const first = r.firstUnackedSeq();
      if (first < low) low = first;
    }
    return low;
  }

  /** Count entries through `throughSeq` as dropped (a batch rejected permanently). */
  recordDropped(count, throughSeq) {
    this.noteEvicted(count, throughSeq);
  }

  /** Return and reset the dropped-entry report, or null when nothing was dropped. */
  takeDropped() {
    if (this.droppedCount === 0) return null;
    const report = { count: this.droppedCount, through: this.discardedThrough };
    this.droppedCount = 0;
    this.persist();
    return report;
  }

  notePersistFailed(ring) {
    this.persistFailed[ring] = (this.persistFailed[ring] || 0) + 1;
  }

  /** Return and reset the failed-write counts, or null when none failed. */
  takePersistFailed() {
    const out = this.persistFailed;
    if (Object.keys(out).length === 0) return null;
    this.persistFailed = {};
    return out;
  }
}
